/**
 * Project Jobs Hook
 * 
 * Manages the relationship between jobs and projects, enabling job organization,
 * reordering, and cross-project management. Projects serve as containers for
 * grouping related jobs, improving organization and navigation.
 * 
 * **Project-Job Relationship:**
 * - Jobs can belong to one project or remain unassigned (orphaned)
 * - Each job has a display order within its project
 * - Jobs can be moved between projects
 * - Projects provide hierarchical organization
 * 
 * **Key Features:**
 * - Fetch jobs for a specific project (ordered by display_order)
 * - Fetch orphaned jobs (no project assignment)
 * - Add jobs to projects with optional ordering
 * - Remove jobs from projects
 * - Reorder jobs within a project
 * - Move jobs between projects
 * 
 * **Job Organization:**
 * - `project_id`: Links job to project (null for orphans)
 * - `project_display_order`: Position within project (drag-drop ordering)
 * - Ordered first by display_order, then by creation date
 * 
 * **Use Cases:**
 * - Project management dashboards
 * - Job organization and grouping
 * - Drag-and-drop job reordering
 * - Multi-project job transfers
 * - Unassigned job management
 * 
 * **Workflow Example:**
 * 1. Fetch orphaned jobs
 * 2. Add selected jobs to project
 * 3. Reorder jobs within project via drag-drop
 * 4. Move jobs to different project as needed
 * 
 * @param {string} projectId - Optional project ID to fetch jobs for
 * 
 * @returns {Object} Project jobs management
 * @returns {Job[]} jobs - Array of jobs (project-specific or orphaned)
 * @returns {boolean} loading - Loading state
 * @returns {string | null} error - Error message if operation failed
 * @returns {Function} fetchProjectJobs - Fetch jobs for specific project
 * @returns {Function} fetchOrphanedJobs - Fetch unassigned jobs
 * @returns {Function} addJobToProject - Assign job to project
 * @returns {Function} removeJobFromProject - Unassign job from project
 * @returns {Function} reorderJobInProject - Change job order within project
 * @returns {Function} moveJobBetweenProjects - Transfer job to different project
 * 
 * @example
 * // Fetch and display project jobs
 * const { jobs, loading, reorderJobInProject } = useProjectJobs(projectId);
 * 
 * <DragDropContext onDragEnd={handleDragEnd}>
 *   {jobs.map((job, index) => (
 *     <JobCard
 *       key={job.id}
 *       job={job}
 *       index={index}
 *       onReorder={(newIndex) => reorderJobInProject(job.id, newIndex)}
 *     />
 *   ))}
 * </DragDropContext>
 * 
 * @example
 * // Manage orphaned jobs
 * const { jobs: orphans, addJobToProject } = useProjectJobs();
 * 
 * useEffect(() => {
 *   fetchOrphanedJobs();
 * }, []);
 * 
 * <Button onClick={() => addJobToProject(selectedJobId, projectId)}>
 *   Add to Project
 * </Button>
 * 
 * @example
 * // Move job between projects
 * const { moveJobBetweenProjects } = useProjectJobs();
 * 
 * await moveJobBetweenProjects(jobId, targetProjectId, newDisplayOrder);
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Job } from '@/lib/types/job';
import { toast } from 'sonner';

export function useProjectJobs(projectId?: string) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProjectJobs = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: jobsError } = await supabase
        .from('jobs')
        .select('*')
        .eq('project_id', id)
        .order('project_display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (jobsError) throw jobsError;

      setJobs((data || []) as Job[]);
    } catch (err: any) {
      console.error('Error fetching project jobs:', err);
      setError(err.message);
      toast.error('Failed to load project jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrphanedJobs = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: jobsError } = await supabase
        .from('jobs')
        .select('*')
        .is('project_id', null)
        .order('created_at', { ascending: false });

      if (jobsError) throw jobsError;

      setJobs((data || []) as Job[]);
    } catch (err: any) {
      console.error('Error fetching orphaned jobs:', err);
      setError(err.message);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const addJobToProject = async (jobId: string, projectId: string, order?: number) => {
    try {
      const updateData: any = { project_id: projectId };
      if (order !== undefined) {
        updateData.project_display_order = order;
      }

      const { error } = await supabase
        .from('jobs')
        .update(updateData)
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job added to project');
      if (projectId) {
        await fetchProjectJobs(projectId);
      }
    } catch (err: any) {
      console.error('Error adding job to project:', err);
      toast.error(err.message || 'Failed to add job to project');
      throw err;
    }
  };

  const removeJobFromProject = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ project_id: null, project_display_order: 0 })
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job removed from project');
      if (projectId) {
        await fetchProjectJobs(projectId);
      }
    } catch (err: any) {
      console.error('Error removing job from project:', err);
      toast.error(err.message || 'Failed to remove job from project');
      throw err;
    }
  };

  const reorderJobInProject = async (jobId: string, newOrder: number) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ project_display_order: newOrder })
        .eq('id', jobId);

      if (error) throw error;

      if (projectId) {
        await fetchProjectJobs(projectId);
      }
    } catch (err: any) {
      console.error('Error reordering job:', err);
      toast.error('Failed to reorder job');
      throw err;
    }
  };

  const moveJobBetweenProjects = async (
    jobId: string,
    toProjectId: string,
    newOrder: number
  ) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({
          project_id: toProjectId,
          project_display_order: newOrder,
        })
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job moved to new project');
      if (projectId) {
        await fetchProjectJobs(projectId);
      }
    } catch (err: any) {
      console.error('Error moving job:', err);
      toast.error('Failed to move job');
      throw err;
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchProjectJobs(projectId);
    }
  }, [projectId]);

  return {
    jobs,
    loading,
    error,
    fetchProjectJobs,
    fetchOrphanedJobs,
    addJobToProject,
    removeJobFromProject,
    reorderJobInProject,
    moveJobBetweenProjects,
  };
}
